import { useEffect, useRef, useState } from 'react'
import { presupuestosApi } from '../api/presupuestos'
import Boton from './Boton'
import Modal from './Modal'
import { colors, text, radius, space, sx } from '../design/tokens'

/**
 * Imágenes de referencia de un presupuesto (sug #95): fotos de cómo se
 * presenta el regalo o el producto. Salen en el PDF que recibe el cliente
 * (mail y link de WhatsApp incluidos), debajo de los renglones.
 *
 * Son de la VERSIÓN: en borrador se suben (botón o arrastrando) y se quitan;
 * en cualquier otro estado solo se ven. Una versión nueva las copia.
 *
 * Props: presupuestoId, imagenes [{id, url, nombre_original}], editable,
 * onCambio(imagenes) — el padre guarda la lista nueva.
 */
const MAXIMO = 10
const MAX_MB = 8
const TIPOS = ['image/jpeg', 'image/png', 'image/webp']

const st = {
  head: { display: 'flex', alignItems: 'center', gap: space.md, marginBottom: space.md },
  titulo: { ...sx.cardTitle, flex: 1 },
  hint: { fontSize: text.sec, color: colors.textSec, margin: 0 },
  error: { fontSize: text.sec, color: colors.err, marginTop: space.sm },
  grilla: { display: 'flex', flexWrap: 'wrap', gap: space.lg },
  mini: {
    position: 'relative', width: 132, height: 132, borderRadius: radius.md,
    border: `1px solid ${colors.border}`, background: colors.bgSoft, overflow: 'hidden', cursor: 'zoom-in',
  },
  img: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  quitar: { position: 'absolute', top: space.xs, right: space.xs },
  drop: {
    border: `1px dashed ${colors.textPlh}`, borderRadius: radius.md, padding: space.xl,
    textAlign: 'center', fontSize: text.sec, color: colors.textSec, marginTop: space.lg,
  },
  dropActivo: { borderColor: colors.link, background: colors.linkBg, color: colors.link },
  grande: { maxWidth: '100%', maxHeight: '75vh', display: 'block', margin: '0 auto' },
}

/** Miniatura: el archivo está en disco privado → se pide con el token y se muestra como blob. */
function useBlobUrl(url) {
  const [src, setSrc] = useState(null)
  useEffect(() => {
    let vivo = true
    let objeto = null
    presupuestosApi.imagenBlob(url)
      .then(blob => { if (vivo) { objeto = URL.createObjectURL(blob); setSrc(objeto) } })
      .catch(() => {})
    return () => { vivo = false; if (objeto) URL.revokeObjectURL(objeto) }
  }, [url])
  return src
}

function Miniatura({ imagen, editable, onQuitar, onVer, trabajando }) {
  const src = useBlobUrl(imagen.url)
  return (
    <div style={st.mini} title={imagen.nombre_original || 'Imagen'} onClick={() => src && onVer(src)}>
      {src ? <img src={src} alt={imagen.nombre_original || ''} style={st.img} /> : null}
      {editable && (
        <span style={st.quitar} onClick={e => e.stopPropagation()}>
          <Boton variante="ghost" sm disabled={trabajando} title="Quitar imagen" onClick={() => onQuitar(imagen)}>✕</Boton>
        </span>
      )}
    </div>
  )
}

export default function ImagenesPresupuesto({ presupuestoId, imagenes = [], editable, onCambio }) {
  const inputRef = useRef(null)
  const [arrastrando, setArrastrando] = useState(false)
  const [trabajando, setTrabajando] = useState(false)
  const [error, setError] = useState(null)
  const [viendo, setViendo] = useState(null)

  // Fuera de borrador y sin imágenes no hay nada que mostrar.
  if (!editable && imagenes.length === 0) return null

  const subir = async (lista) => {
    setError(null)
    const archivos = Array.from(lista || [])
    if (archivos.length === 0) return
    const invalido = archivos.find(f => !TIPOS.includes(f.type))
    if (invalido) { setError(`"${invalido.name}" no es una imagen JPG, PNG o WebP.`); return }
    const pesado = archivos.find(f => f.size > MAX_MB * 1024 * 1024)
    if (pesado) { setError(`"${pesado.name}" pesa más de ${MAX_MB} MB.`); return }
    if (imagenes.length + archivos.length > MAXIMO) { setError(`Un presupuesto lleva hasta ${MAXIMO} imágenes.`); return }
    setTrabajando(true)
    try {
      const { data } = await presupuestosApi.subirImagenes(presupuestoId, archivos)
      onCambio?.(data.imagenes)
    } catch (e) {
      setError(e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' · ') || 'No se pudo subir la imagen.')
    } finally {
      setTrabajando(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const quitar = async (imagen) => {
    if (!window.confirm('¿Quitar esta imagen del presupuesto?')) return
    setError(null)
    setTrabajando(true)
    try {
      const { data } = await presupuestosApi.quitarImagen(presupuestoId, imagen.id)
      onCambio?.(data.imagenes)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo quitar la imagen.')
    } finally {
      setTrabajando(false)
    }
  }

  const dropProps = editable ? {
    onDragOver: e => { e.preventDefault(); setArrastrando(true) },
    onDragLeave: () => setArrastrando(false),
    onDrop: e => { e.preventDefault(); setArrastrando(false); subir(e.dataTransfer.files) },
  } : {}

  return (
    <div {...dropProps}>
      <div style={st.head}>
        <h3 style={st.titulo}>Imágenes para el cliente</h3>
        {editable && (
          <>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
              onChange={e => subir(e.target.files)} />
            <Boton variante="secondary" sm disabled={trabajando || imagenes.length >= MAXIMO}
              onClick={() => inputRef.current?.click()}>
              {trabajando ? 'Subiendo…' : '+ Agregar imágenes'}
            </Boton>
          </>
        )}
      </div>

      {imagenes.length > 0 && (
        <div style={st.grilla}>
          {imagenes.map(img => (
            <Miniatura key={img.id} imagen={img} editable={editable} trabajando={trabajando}
              onQuitar={quitar} onVer={setViendo} />
          ))}
        </div>
      )}

      {editable && (
        <div style={{ ...st.drop, ...(arrastrando ? st.dropActivo : {}) }}>
          Arrastrá fotos acá (JPG, PNG o WebP, hasta {MAX_MB} MB c/u, máximo {MAXIMO}).
          Salen en el PDF que recibe el cliente, debajo de los renglones.
        </div>
      )}
      {!editable && <p style={{ ...st.hint, marginTop: space.md }}>Salen en el PDF que recibe el cliente. Para cambiarlas, creá una nueva versión.</p>}
      {error && <div style={st.error}>{error}</div>}

      {viendo && (
        <Modal title="Imagen del presupuesto" onClose={() => setViendo(null)} maxWidth="900px">
          <img src={viendo} alt="" style={st.grande} />
        </Modal>
      )}
    </div>
  )
}
